// ─── View Mode ────────────────────────────────────────────────

export type ViewMode = 'default' | 'territory-group' | 'state-badge' | 'state-cone' | 'state-icon' | 'state-bubble'

// ─── Color Constants ──────────────────────────────────────────

export const COLOR_BAR = ['#CEE3FF', '#5a9bf0', '#0d47a1']

export const COLOR_TYPES: any[] = [
  { 'Practice': '#FF9A3D', flag: false },
  { 'ASC': '#93A0C8', flag: false },
  { 'Hospital': '#4AB3FF', flag: false },
  { 'Purchasing Practice': '#645DD7', flag: false },
  { 'Purchasing  Practice': '#645DD7', flag: false },
  { 'Distributor': '#0d47a1', flag: false },
  { 'Not Activated': '#ffffff', flag: true },
]

export const BUBBLE_COLOR_TYPES: any[] = [
  { 'Group': '#645DD7', flag: false },
  { 'Hospital': '#4AB3FF', flag: false },
]

// ─── Hierarchy Level (territory-group mode) ───────────────────

export interface HierarchyLevel {
  level: string                 // e.g. 'employee-account'
  keys: string[]                // fields to group by at this level
  labelKey: string              // field used as label
  prescriberKeys: {
    id: string
    name: string
  }
}

// ─── Per-Config Settings ──────────────────────────────────────

export interface ViewModeConfig {
  id: string
  label: string
  mode: ViewMode

  // api
  apiKey: string

  // data
  normalizer: 'zip' | 'state'
  groupBy: string
  dataSource: string

  // colors
  colorBar: string[]
  colorTypes: any[]

  // data field mappings
  labelFields: string[]
  dataFields: string[]
  valueField?: string
  colorField?: string
  flagField?: string

  // raw-field → canonical-name mappings (state-bubble; renderer reads canonical names)
  cityField?: string
  stateField?: string
  zipField?: string
  addressField?: string
  idField?: string
  siteField?: string

  badgeTextColor?: string
  badgeBg?: string
  rangeLabel?: string;
  // hierarchy (territory-group mode)
  hierarchy?: HierarchyLevel[]

  // map view
  highlight: string
  useDeduplicate: boolean
  tooltipTemplate: string
  slopeChartTrigger: boolean,
  // UI toggles (derived from config if not explicitly set)
  showRangeBar?: boolean
  showLegendsLabel?: boolean
}

// ─── Default Config Sets ──────────────────────────────────────

export const DEFAULT_CONFIGS: ViewModeConfig[] = [
  {
    id: 'employee-view',
    label: 'Employee View',
    mode: 'default',
    apiKey: '',
    normalizer: 'zip',
    groupBy: 'employee_key',
    dataSource: 'rawData',
    colorBar: [],
    colorTypes: [],
    labelFields: ['employee_first_name', 'employee_last_name'],
    dataFields: ['employee_key', 'employee_first_name', 'employee_last_name', 'territory_key'],
    highlight: 'counties',
    useDeduplicate: false,
    slopeChartTrigger: false,
    tooltipTemplate: 'custom',
  },
  {
    id: 'territory-group-view',
    label: 'Territory Group View',
    mode: 'territory-group',
    apiKey: '',
    normalizer: 'zip',
    groupBy: 'employee_key',
    dataSource: 'accountViewData',
    colorBar: [],
    colorTypes: [],
    labelFields: ['employee_first_name', 'employee_last_name'],
    dataFields: [],
    hierarchy: [
      {
        level: 'employee-account',
        keys: ['employee_key', 'account_id'],
        labelKey: 'account_name',
        prescriberKeys: { id: 'prescriber_id', name: 'prescriber_name' },
      },
      {
        level: 'employee-account-sub_account',
        keys: ['employee_key', 'account_id', 'sub_account_id'],
        labelKey: 'sub_account_name',
        prescriberKeys: { id: 'prescriber_id', name: 'prescriber_name' },
      },
    ],
    highlight: 'counties',
    useDeduplicate: true,
    slopeChartTrigger: false,
    tooltipTemplate: 'custom',
  },
  {
    id: 'state-badge-view',
    label: 'State Badge View',
    mode: 'state-badge',
    apiKey: '100925',
    normalizer: 'state',
    groupBy: 'state',
    dataSource: 'stateBadgeViewData',
    colorBar: COLOR_BAR,
    colorTypes: [],
    labelFields: ['state'],
    dataFields: ['state', 'total_patients', 'no_soc'],
    valueField: 'total_patients',
    highlight: 'counties',
    badgeBg: '#ffffff',
    badgeTextColor: '#000000',
    useDeduplicate: false,
    slopeChartTrigger: true,
    rangeLabel: 'Patient Count',
    tooltipTemplate: 'custom',
  },
  {
    id: 'state-cone-view',
    label: 'Site-of-Care Cone View',
    mode: 'state-cone',
    apiKey: '100932',
    normalizer: 'zip',
    groupBy: 'oracle_account_id',
    dataSource: 'stateConeViewData',
    colorBar: COLOR_BAR,
    colorTypes: COLOR_TYPES,
    labelFields: ['site_of_care'],
    dataFields: [],
    valueField: 'total_patients',
    colorField: 'practice_type',
    flagField: 'active_flg',
    highlight: 'counties',
    useDeduplicate: true,
    slopeChartTrigger: false,
    tooltipTemplate: 'coneview',
  },
  {
    id: 'state-icon-view',
    label: 'Site-of-Care Icon View',
    mode: 'state-icon',
    apiKey: '100932',
    normalizer: 'zip',
    groupBy: 'oracle_account_id',
    dataSource: 'stateConeViewData',
    colorBar: ['#EAF3FF', '#81AEEE'],
    colorTypes: [],
    labelFields: ['site_of_care'],
    dataFields: [],
    valueField: 'total_patients',
    flagField: '',
    zipField: 'zip_cd',
    highlight: 'counties',
    useDeduplicate: true,
    slopeChartTrigger: false,
    rangeLabel: 'Patient Count',
    tooltipTemplate: 'coneview',
  },
  {
    id: 'state-bubble-view',
    label: 'Site-of-Care Bubble View',
    mode: 'state-bubble',
    apiKey: '100932',
    normalizer: 'zip',
    groupBy: 'group_key',
    dataSource: 'stateBubbleViewData',
    colorBar: [],
    colorTypes: BUBBLE_COLOR_TYPES,
    labelFields: ['group_name'],
    dataFields: [],
    valueField: 'total_pat_grp_prac',
    colorField: 'practice_type',
    cityField: 'practice_city',
    stateField: 'practice_state',
    zipField: 'practice_zip',
    addressField: 'practice_address',
    idField: 'group_key',
    siteField: 'group_name',
    highlight: 'counties',
    useDeduplicate: true,
    slopeChartTrigger: false,
    tooltipTemplate: 'coneview',
  },
]

// ─── Config Factory ───────────────────────────────────────────

export default function configFactory(cfg: ViewModeConfig) {
  return {
    components: [{
      id: 'territory-map',
      view: {
        tooltip: 'territory-map-tooltip',
      },
    }, {
      id: 'territory-map-tooltip',
      data: {
        source: 'service',
      },
      view: {
        template: cfg.tooltipTemplate,
      },
    }],
  }
}
